import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";

const KEY = "vt_news_popup";              // localStorage: "subscribed" | "dismissed:<ts>" | "shown:<ts>"
const SESSION_KEY = "vt_news_popup_seen"; // sessionStorage: once seen this session, don't reshow
const FOURTEEN_DAYS = 14 * 24 * 60 * 60 * 1000;

function suppressed() {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return true;
    const v = localStorage.getItem(KEY);
    if (!v) return false;
    if (v === "subscribed") return true;           // already a subscriber on this device
    const [kind, ts] = v.split(":");
    if (kind === "dismissed" || kind === "shown") {
      return Date.now() - Number(ts || 0) < FOURTEEN_DAYS; // at most once / 14 days per device
    }
  } catch { /* private mode / disabled storage */ }
  return false;
}

export default function NewsletterPopup() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onHome = location.pathname === "/";                 // homepage has 3 signup points — no popup
  const onAdmin = location.pathname.startsWith("/admin");
  const onCheckout = location.pathname.startsWith("/payment");
  const onSnap = location.pathname === "/snap-card" || location.pathname.startsWith("/coach-ready/learn"); // signup page / paid course
  const disabled = onHome || onAdmin || onCheckout || onSnap;

  useEffect(() => {
    if (disabled) return;
    if (suppressed()) return;

    let fired = false;
    const trigger = () => {
      if (fired) return;
      fired = true;
      try { localStorage.setItem(KEY, `shown:${Date.now()}`); } catch { /* ignore */ }
      setOpen(true);
      cleanup();
    };

    // Show only after the visitor scrolls 50% of the page (no time-based trigger).
    const onScroll = () => {
      const el = document.documentElement;
      const scrollable = el.scrollHeight - el.clientHeight;
      if (scrollable > 0 && el.scrollTop / scrollable >= 0.5) trigger();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    function cleanup() {
      window.removeEventListener("scroll", onScroll);
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const dismiss = () => {
    try {
      localStorage.setItem(KEY, `dismissed:${Date.now()}`);
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch { /* ignore */ }
    setOpen(false);
  };

  const onSubscribed = () => {
    try {
      localStorage.setItem(KEY, "subscribed");
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch { /* ignore */ }
    setTimeout(() => setOpen(false), 2600);
  };

  if (!open || disabled) return null;

  return (
    <div
      className="fixed z-[60] inset-x-3 bottom-3 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[380px]"
      role="dialog"
      aria-label="Join the Voluntold newsletter"
      data-testid="newsletter-popup"
    >
      <div className="relative bg-[#F5F0EA] border-2 border-[#222222] shadow-2xl">
        <div className="bg-[#222222] px-5 py-3 flex items-center justify-between border-b-4 border-[#DB9130]">
          <img src="/brand/logos/sideline-lockup-on-dark.svg" alt="The Sideline by VoluntoldHQ" className="h-8 w-auto max-w-[300px]" />
          <button
            onClick={dismiss}
            aria-label="Close"
            data-testid="newsletter-popup-close"
            className="text-[#A9B6BF] hover:text-[#F5F0EA] transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          <NewsletterSignup
            source="popup"
            tone="light"
            testId="newsletter-popup-signup"
            heading="One coaching read a week."
            sub="Youth sports from both sides of the whistle — coaches, players, parents, culture. No spam, unsubscribe anytime."
            onSuccess={onSubscribed}
          />
        </div>
      </div>
    </div>
  );
}
